import { useCallback, useEffect, useState } from "react";

/**
 * Persistent Footage Search selection — the user's "cart" of clips
 * they've ticked for an eventual bundle download. Stored in
 * localStorage so an accidental refresh / browser restart doesn't
 * lose their progress on building a multi-search project.
 *
 * One slot per browser (no cross-device sync — that'd need a
 * server-side store keyed on user). Cleared explicitly via the tray
 * "Clear all" button.
 */

const STORAGE_KEY = "fm.footageSearch.cart.v1";
// Separate storage key for the "include accompanying audio" toggles so the
// shape of fm.footageSearch.cart.v1 stays a plain SelectedItem[]. Lets us
// evolve the audio feature without a migration on the main cart slot.
const AUDIO_STORAGE_KEY = "fm.footageSearch.cart.audio.v1";

export interface SelectedItem {
  id: number;
  title: string;
  driveFileId: string | null;
  subjectKind: string | null;
  /**
   * "video" (default) or "audio". Persisted so the bundle download can
   * route audio rows into the sibling Audio - MM.DD.YY/ folder. Older
   * cart entries (pre-feature) deserialize as "video" — accurate since
   * audio uploads didn't exist before.
   */
  mediaKind?: "video" | "audio";
}

function load(): Map<number, SelectedItem> {
  if (typeof localStorage === "undefined") return new Map();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const arr = JSON.parse(raw) as SelectedItem[];
    if (!Array.isArray(arr)) return new Map();
    // Validate shape defensively — bad localStorage state shouldn't
    // crash the page. Drop anything missing the required fields.
    const entries: Array<[number, SelectedItem]> = [];
    for (const item of arr) {
      if (
        item &&
        typeof item.id === "number" &&
        typeof item.title === "string"
      ) {
        entries.push([
          item.id,
          {
            id: item.id,
            title: item.title,
            driveFileId: typeof item.driveFileId === "string" ? item.driveFileId : null,
            subjectKind: typeof item.subjectKind === "string" ? item.subjectKind : null,
            mediaKind: item.mediaKind === "audio" ? "audio" : "video",
          },
        ]);
      }
    }
    return new Map(entries);
  } catch {
    return new Map();
  }
}

function save(items: Map<number, SelectedItem>): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(Array.from(items.values())),
    );
  } catch {
    /* quota exceeded / storage disabled — silently fail, user keeps
       their cart in-memory for the session at least. */
  }
}

// ─── Accompanying-audio toggles (parallel state) ────────────────────────────
// One boolean per video-row ID. `true` means "include the audio files that
// were detected as accompanying this video clip in the bundle download".
// The actual audio IDs aren't stored here — they're resolved fresh from
// the result card's `accompanyingAudio` list at download time. Keeps the
// localStorage payload small and means stale matches (e.g. a re-analysis
// changed the link) don't haunt the user.

function loadAudioToggles(): Map<number, boolean> {
  if (typeof localStorage === "undefined") return new Map();
  try {
    const raw = localStorage.getItem(AUDIO_STORAGE_KEY);
    if (!raw) return new Map();
    const obj = JSON.parse(raw) as Record<string, boolean>;
    if (!obj || typeof obj !== "object") return new Map();
    const entries: Array<[number, boolean]> = [];
    for (const [k, v] of Object.entries(obj)) {
      const id = Number(k);
      if (Number.isFinite(id) && v === true) entries.push([id, true]);
    }
    return new Map(entries);
  } catch {
    return new Map();
  }
}

function saveAudioToggles(toggles: Map<number, boolean>): void {
  if (typeof localStorage === "undefined") return;
  try {
    const obj: Record<string, boolean> = {};
    for (const [id, v] of toggles) if (v) obj[String(id)] = true;
    localStorage.setItem(AUDIO_STORAGE_KEY, JSON.stringify(obj));
  } catch {
    /* same fallback as save() above */
  }
}

/**
 * Hook that owns the selection cart. Reads from localStorage on mount,
 * persists every change. Returns the Map plus three actions
 * (toggle / remove / clear).
 */
export function useSelectionCart(): {
  selected: Map<number, SelectedItem>;
  toggle: (item: SelectedItem) => void;
  /**
   * Union-add — every input item is inserted into the cart if not
   * already present. Never removes. Used by the "add session mates
   * to bundle" button in Footage Search, where toggle-semantics
   * would incorrectly remove already-carted siblings. Callers that
   * need a "how many were newly added" count should compute it
   * against `selected` before invoking (the setter can run twice
   * under React strict mode, so counting inside is unreliable).
   */
  addMany: (items: SelectedItem[]) => void;
  remove: (id: number) => void;
  clear: () => void;
  /**
   * Whether "include accompanying audio file(s)" is checked for a given
   * video row. False for any video the user hasn't explicitly opted in,
   * always false for audio rows (they're the attachment, never the host).
   */
  audioIncluded: (videoId: number) => boolean;
  /** Flip the accompanying-audio toggle for one video row. */
  toggleAudioInclusion: (videoId: number) => void;
} {
  // Lazy initializer reads localStorage exactly once on mount.
  const [selected, setSelected] = useState<Map<number, SelectedItem>>(load);
  const [audioToggles, setAudioToggles] = useState<Map<number, boolean>>(loadAudioToggles);

  // Persist whenever the cart contents change. Identity-of-the-Map
  // changes on every setSelected call (we always return a new Map),
  // so this fires reliably.
  useEffect(() => {
    save(selected);
  }, [selected]);

  useEffect(() => {
    saveAudioToggles(audioToggles);
  }, [audioToggles]);

  // Cross-tab sync — if the same user has Footage Search open in
  // two tabs, ticking a clip in one updates the other. The "storage"
  // event fires only in OTHER tabs, not the originating one, which
  // is exactly what we want.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setSelected(load());
      else if (e.key === AUDIO_STORAGE_KEY) setAudioToggles(loadAudioToggles());
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const toggle = useCallback((item: SelectedItem) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
        // Removing a video from the cart drops its accompanying-audio
        // toggle too — the toggle is meaningless without the host clip.
        setAudioToggles((cur) => {
          if (!cur.has(item.id)) return cur;
          const m = new Map(cur);
          m.delete(item.id);
          return m;
        });
      } else next.set(item.id, item);
      return next;
    });
  }, []);

  // Union-add path. No count returned — the setSelected updater can
  // run twice under React strict mode, so a count computed inside is
  // unreliable. Callers derive it from `selected` before calling.
  const addMany = useCallback((items: SelectedItem[]) => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const item of items) {
        if (!next.has(item.id)) next.set(item.id, item);
      }
      return next;
    });
  }, []);

  const remove = useCallback((id: number) => {
    setSelected((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
    setAudioToggles((cur) => {
      if (!cur.has(id)) return cur;
      const m = new Map(cur);
      m.delete(id);
      return m;
    });
  }, []);

  const clear = useCallback(() => {
    setSelected(new Map());
    setAudioToggles(new Map());
  }, []);

  const audioIncluded = useCallback(
    (videoId: number) => audioToggles.get(videoId) === true,
    [audioToggles],
  );

  const toggleAudioInclusion = useCallback((videoId: number) => {
    setAudioToggles((cur) => {
      const m = new Map(cur);
      if (m.get(videoId)) m.delete(videoId);
      else m.set(videoId, true);
      return m;
    });
  }, []);

  return {
    selected,
    toggle,
    addMany,
    remove,
    clear,
    audioIncluded,
    toggleAudioInclusion,
  };
}
